import { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { getCacheItem, setCacheItem, removeCacheItem } from "../utils/cache";

const CONNECTION_STATUS_KEY = "sleeper-connection-status";
const LEAGUES_CACHE_KEY = "sleeper-leagues";

export function useSleeperConnection() {
    const { user } = useUser();
    const [isConnected, setIsConnected] = useState(false);
    const [leagues, setLeagues] = useState([]);
    const [lastImportTime, setLastImportTime] = useState(null);
    
    // Check connection status on mount and when user changes
    useEffect(() => {
        if (user) {
            const connectionStatus = getCacheItem(CONNECTION_STATUS_KEY);
            const cachedLeagues = getCacheItem(LEAGUES_CACHE_KEY);
            
            if (connectionStatus && cachedLeagues) {
                // Handle both direct array and paginated response formats
                const leaguesArray = Array.isArray(cachedLeagues) ? cachedLeagues : (cachedLeagues.results || []);
                setIsConnected(true);
                setLeagues(leaguesArray);
                setLastImportTime(connectionStatus.lastImportTime);
            }
        } else {
            // Clear state when user signs out
            setIsConnected(false);
            setLeagues([]);
            setLastImportTime(null);
        }
    }, [user]);
    
    const markAsConnected = (leaguesData, importTime = new Date().toISOString()) => {
        const connectionStatus = {
            connected: true,
            lastImportTime: importTime,
            userId: user?.id,
        };
        
        setCacheItem(CONNECTION_STATUS_KEY, connectionStatus, 24 * 60 * 60 * 1000); // 24 hours
        setCacheItem(LEAGUES_CACHE_KEY, leaguesData, 10 * 60 * 1000); // 10 minutes
        
        setIsConnected(true);
        setLeagues(leaguesData);
        setLastImportTime(importTime);
    };
    
    const disconnect = () => {
        removeCacheItem(CONNECTION_STATUS_KEY);
        removeCacheItem(LEAGUES_CACHE_KEY);
        removeCacheItem("sleeper-last-username");
        removeCacheItem("sleeper-import-summary");
        
        setIsConnected(false);
        setLeagues([]);
        setLastImportTime(null);
    };
    
    const refreshLeagues = async () => {
        if (!user) return;
        
        try {
            const headers = {
                "Content-Type": "application/json",
                "X-Clerk-User-Id": user.id,
                "X-Clerk-Email": user.primaryEmailAddress?.emailAddress || "",
                "X-Clerk-Username": user.username || "",
            };
            
            const apiSecret = process.env.REACT_APP_API_SHARED_SECRET || process.env.NEXT_PUBLIC_API_SHARED_SECRET;
            if (apiSecret) {
                headers["X-API-KEY"] = apiSecret;
            }
            
            let baseUrl = process.env.NODE_ENV === "development" ? "" : "https://weez-nfl-data.vercel.app";
            const response = await fetch(`${baseUrl}/api/leagues/`, {
                method: "GET",
                headers,
            });
            
            if (response.ok) {
                const leaguesData = await response.json();
                // Handle both direct array and paginated response formats
                const leaguesArray = Array.isArray(leaguesData) ? leaguesData : (leaguesData.results || []);
                markAsConnected(leaguesArray);
                return leaguesArray;
            }
        } catch (error) {
            console.error("Failed to refresh leagues:", error);
        }
        
        return leagues;
    };
    
    return {
        isConnected,
        leagues,
        lastImportTime,
        markAsConnected,
        disconnect,
        refreshLeagues,
    };
}
